import {Popper, useAutocomplete} from '@mui/material';
import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';

import {
  ChevronSmallDown,
  ChevronSmallUp,
  Close,
  Filter,
  LoadingIcon,
} from '@ELib/assets/images';
import {classy} from '@ELib/utilities/HelperFunctions';
import {isStringNotBlank} from '@ELib/utilities/string';

import {AnimationWrapper} from '../../AnimationWrapper';
import {EIconButton, EIconSmallButton} from '../../EButtons';
import {FormInputWrapper} from '../FormInputWrapper';
import {stopKeyboardEventPropagation} from '../keyboardEventPropgation';

import {
  AdornmentWrapper,
  CenteredLi,
  FilterWrapper,
  StyledListbox,
  StyledTextField,
} from './EAutocomplete.styles';
import {
  CategoryWithOptions,
  EAutocompleteOption,
  EAutocompleteProps,
  isAutocompleteOption,
  MAIN_CATEGORY,
  OnChangeEventType,
} from './EAutocomplete.types';
import {EAutocompleteCategories} from './EAutocompleteCategories';

// eslint-disable-next-line complexity
export function EAutocomplete({
  label,
  labelInfo,
  bottomText,
  name,
  value,
  options: unsortedOptions,
  onChange,
  onBlur,
  allowCustomInput,
  autoFocus,
  disabled,
  ariaLabel,
  dynamicDropdownWidth,
  isLoading,
  error,
  errorMessage,
  onSearchQueryChange,
  noOptionFiltering,
  hasMoreBefore,
  hasMoreAfter,
  onLoadMoreBefore,
  onLoadMoreAfter,
  placeholder,
  categories,
  openOnFocus,
  inputRef,
  align = 'left',
  disableOpenOnAutofocus = false,
  tabfocusableClear = true,
  allowEnterSubmit = false,
  clearOnSelection = false,
  hideClearButton = false,
  moreOptionPadding = false,
  disableClearSearchQueryOnChange = false,
  disableClearSearchQueryOnBlur = false,
  disableSingleOptionSelection = false,
  takeHighlightedOptionOnBlur = false,
  required = false,
  multiple = false,
  onFilterClick,
  optionFilterInfo,
}: EAutocompleteProps): React.ReactElement {
  const {t} = useTranslation('lib');
  const [highlightedOption, setHighlightedOption] =
    useState<EAutocompleteOption | null>(null);
  const [lastSelectedOption, setLastSelectedOption] =
    useState<EAutocompleteOption | null>(null);
  // The useAutocomplete hook behaves strangely when the previous text input string does
  // not have anything in common with the new text input string and "eats" the new one
  // by emitting a "reset" event. For this reason we need to track the text input value manually.
  const [internalTextValue, setInternalTextValue] = useState('');

  const optionsSortedByCategory = useMemo(() => {
    const getCategoryLabelIndex = (option: EAutocompleteOption) =>
      (categories || [MAIN_CATEGORY]).findIndex(
        (category) => category.name === option.categoryName
      );

    // must not sort in place - `unsortedOptions` is the caller's array
    return [...unsortedOptions].sort((a, b) => {
      const catA = getCategoryLabelIndex(a);
      const catB = getCategoryLabelIndex(b);
      if (catA === catB) {
        return 0;
      }
      if (catA === -1) {
        return 1;
      }
      if (catB === -1) {
        return -1;
      }
      return catA - catB;
    });
  }, [unsortedOptions, categories]);

  const isRequiredSingleValue = useMemo(
    () =>
      unsortedOptions.length === 1 &&
      !unsortedOptions[0]?.disabled &&
      required &&
      !disableSingleOptionSelection,
    [unsortedOptions, required, disableSingleOptionSelection]
  );

  const {selectedValue, valueAsOption} = useMemo(() => {
    const option = optionsSortedByCategory.find((o) => o.value === value);
    const isSelected = Boolean(option) || Boolean(allowCustomInput && value);
    return {
      valueAsOption: option,
      selectedValue: isSelected ? value : null,
    };
  }, [allowCustomInput, optionsSortedByCategory, value]);

  useEffect(() => {
    // Remove text query when value has been set to undefined.
    if (!value) {
      setInternalTextValue('');
    }
    // `lastSelectedOption` is only read on blur, so it is tracked here. Setting
    // it while rendering re-rendered the component on every render, because the
    // callers rebuild their option objects on every render (LEXIAN-8417). Only a
    // different option is stored, not a different object for the same one. The
    // display text is part of that comparison because blur writes it back.
    if (valueAsOption) {
      setLastSelectedOption((current) =>
        current?.value === valueAsOption.value &&
        current.displayText === valueAsOption.displayText
          ? current
          : valueAsOption
      );
    }
  }, [value, valueAsOption]);

  const triggerOnChange = useCallback(
    (newValue: string, type: OnChangeEventType) => {
      onChange?.({
        type,
        target: {
          name,
          value: newValue,
        },
      } as React.ChangeEvent<HTMLSelectElement>);
    },
    [name, onChange]
  );

  // Emulate the native select event so that it can be seamlessly integrated into forms
  const compatibilityOnChange = useCallback(
    (option: EAutocompleteOption | string | null) => {
      let convertedValue = '';
      if (isAutocompleteOption(option)) {
        convertedValue = option.value;
      } else if (allowCustomInput) {
        convertedValue = option || '';
      }
      triggerOnChange(convertedValue, OnChangeEventType.Change);
      if (!disableClearSearchQueryOnChange) {
        onSearchQueryChange?.('');
      }

      if (clearOnSelection) {
        setLastSelectedOption(null);
        setInternalTextValue('');
        triggerOnChange('', OnChangeEventType.ClearOnSelection);
        onSearchQueryChange?.('');
      }
    },
    [
      allowCustomInput,
      triggerOnChange,
      disableClearSearchQueryOnChange,
      clearOnSelection,
      onSearchQueryChange,
    ]
  );

  // Emulate the native select event so that it can be seamlessly integrated into forms
  const compatibilityOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Since we are manually handling the behaviour of the internal text value we also need to set
    // it to the last selected option (if available) or the empty string (if no valid option was selected previously)
    // on blur and propagate that change to the calling component via `onSearchQueryChange`.
    // All inputs are valid with the allowCustomInput which is why it is not necessary in that case.
    if (!allowCustomInput && !takeHighlightedOptionOnBlur) {
      const newInputValue =
        value === '' ? '' : lastSelectedOption?.displayText || '';
      setInternalTextValue(newInputValue);
      if (!disableClearSearchQueryOnBlur || !newInputValue) {
        onSearchQueryChange?.('');
      }
    }

    // This is necessary to propagate the text input as a value if custom text is allowed
    if (internalTextValue && allowCustomInput && !takeHighlightedOptionOnBlur) {
      compatibilityOnChange(internalTextValue);
    }

    if (takeHighlightedOptionOnBlur && isStringNotBlank(internalTextValue)) {
      compatibilityOnChange(effectiveHighlightRef.current);
    }

    if (onBlur) {
      if (
        takeHighlightedOptionOnBlur &&
        effectiveHighlightRef.current &&
        isStringNotBlank(internalTextValue)
      ) {
        e.target.value = effectiveHighlightRef.current.value;
      } else {
        e.target.value =
          optionsSortedByCategory.find((o) => o.displayText === e.target.value)
            ?.value || (allowCustomInput ? e.target.value : '');
      }
      onBlur(e);
    }
  };

  // we need to keep track if preselection of single option was done already, it may only be done initially
  const [didSetSingleOption, setDidSetSingleOption] = useState<boolean>(false);
  useEffect(
    () => {
      // beware of side effects
      if (
        isRequiredSingleValue &&
        !didSetSingleOption &&
        unsortedOptions[0] &&
        !unsortedOptions[0].isPlaceholderOption
      ) {
        if (!value && !internalTextValue) {
          triggerOnChange(unsortedOptions[0].value, OnChangeEventType.Change);
        }
        setDidSetSingleOption(true);
      }
    },
    // no need to trigger on internalTextValue
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      compatibilityOnChange,
      didSetSingleOption,
      isRequiredSingleValue,
      unsortedOptions,
    ]
  );

  // wasFocusedOnAutofocus tracks if on autoFocus was applied to the input element or if not necessary
  // later we set the shouldOpenOnFocus variable depending if the component was disallowed to open itself on autofocus
  // wasFocusedOnAutofocus gets set to true as soon as the input gets focus the first time (during the autofocus event)
  const wasFocusedOnAutofocus = useRef(!autoFocus);
  const shouldOpenOnFocus =
    (openOnFocus &&
      (wasFocusedOnAutofocus.current || !disableOpenOnAutofocus)) ||
    false;

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    getClearProps,
    getPopupIndicatorProps,
    groupedOptions,
    anchorEl,
    setAnchorEl,
    popupOpen,
    dirty,
  } = useAutocomplete<EAutocompleteOption, undefined, undefined, boolean>({
    componentName: 'EAutocomplete',
    inputValue: internalTextValue,
    onInputChange: (_, newInputValue, reason) => {
      // An event with reason "reset" is emitted when a value is selected and
      // also when the previous input does not have anything in common with the
      // current. Since we are manually handling the behaviour we need to set the
      // internal text value, emit the search query change and the onChange events.
      if (reason !== 'reset') {
        setInternalTextValue(newInputValue || '');
        if (onSearchQueryChange) {
          onSearchQueryChange(newInputValue || '');
        }
      } else if (newInputValue) {
        setInternalTextValue(newInputValue);
      }
    },
    value: selectedValue,
    onChange: (_event, option) => compatibilityOnChange(option),
    freeSolo: allowCustomInput,
    autoSelect: false,
    openOnFocus: shouldOpenOnFocus,
    options: optionsSortedByCategory,
    disabled,
    getOptionLabel: (option: EAutocompleteOption | string) =>
      isAutocompleteOption(option)
        ? option.displayText
        : valueAsOption?.displayText || option,
    getOptionDisabled: (option: EAutocompleteOption | string) =>
      (isAutocompleteOption(option) ? option.disabled : undefined) || false,
    isOptionEqualToValue: (
      option: EAutocompleteOption | string,
      compareValue?: EAutocompleteOption | string | null
    ) =>
      (isAutocompleteOption(option) ? option.value : option) ===
      (compareValue && isAutocompleteOption(compareValue)
        ? compareValue.value
        : compareValue),
    filterOptions: noOptionFiltering ? (options) => options : undefined,
    autoHighlight: true,
    onHighlightChange: (_event, option) => setHighlightedOption(option),
  });

  const optionsToDisplay = groupedOptions as EAutocompleteOption[];

  const effectiveHighlightRef = useEffectiveHighlight(
    highlightedOption,
    optionsToDisplay
  );

  const categoriesWithOptions = useMemo(
    (): CategoryWithOptions[] =>
      (categories || [MAIN_CATEGORY]).map((cat) => ({
        name: cat.name,
        topLabel: cat.topLabel,
        mainLabel: cat.mainLabel,
        options: optionsToDisplay.filter(
          (option) => option.categoryName === cat.name
        ),
      })),
    [categories, optionsToDisplay]
  );

  const hasClearIcon =
    !hideClearButton && !disabled && dirty && !isRequiredSingleValue;

  const inputProps = getInputProps();

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const onChangeValue = allowCustomInput
        ? internalTextValue
        : highlightedOption ?? firstSelectableOption(optionsToDisplay);
      if (event.key === 'Tab' && popupOpen) {
        compatibilityOnChange(onChangeValue);
      } else if (event.key !== 'Enter' && allowEnterSubmit) {
        if (event.key === 'Tab' && internalTextValue && allowCustomInput) {
          compatibilityOnChange(onChangeValue);
        }
        inputProps.onKeyDown?.(event);
      } else if (
        (event.key === 'Enter' || event.key === 'Tab') &&
        internalTextValue &&
        allowCustomInput
      ) {
        compatibilityOnChange(onChangeValue);
      }
    },
    [
      allowCustomInput,
      allowEnterSubmit,
      compatibilityOnChange,
      inputProps,
      highlightedOption,
      optionsToDisplay,
      internalTextValue,
      popupOpen,
    ]
  );

  const handleFilterClick = (e: React.MouseEvent): void => {
    if (onFilterClick) {
      e.stopPropagation();
      onFilterClick(e);
    }
  };

  const showPopup = !(allowCustomInput && optionsToDisplay.length === 0);

  return (
    <>
      <FormInputWrapper
        {...getRootProps()}
        bottomText={bottomText}
        className='EAutocomplete'
        data-testid={`EAutocomplete-${name}`}
        error={error}
        errorMessage={errorMessage}
        label={label}
        labelInfo={labelInfo}
        labelProps={getInputLabelProps()}
      >
        <StyledTextField
          autoFocus={autoFocus}
          className={classy({
            alignRight: align === 'right',
            'no-padding-right': onFilterClick !== undefined,
          })}
          disabled={disabled}
          error={error}
          inputProps={{
            ...inputProps,
            onFocus: (e) => {
              inputProps.onFocus?.(e as React.FocusEvent<HTMLInputElement>);
              wasFocusedOnAutofocus.current = true;
            },
            onKeyDown: stopKeyboardEventPropagation,
            value: internalTextValue,
            'aria-label': ariaLabel,
          }}
          InputProps={{
            placeholder,
            ref: setAnchorEl,
            endAdornment: (
              <>
                {hasClearIcon ? (
                  <EIconSmallButton
                    {...(getClearProps() as React.HtmlHTMLAttributes<HTMLButtonElement>)}
                    tabIndex={tabfocusableClear ? 0 : -1}
                    testId='clear'
                  >
                    <Close />
                  </EIconSmallButton>
                ) : null}
                <AdornmentWrapper
                  {...getPopupIndicatorProps()}
                  className={disabled ? 'disabled' : undefined}
                  type='button'
                >
                  {popupOpen ? <ChevronSmallUp /> : <ChevronSmallDown />}
                </AdornmentWrapper>
                {onFilterClick ? (
                  <FilterWrapper>
                    <EIconButton tabIndex={-1} onClick={handleFilterClick}>
                      <Filter />
                    </EIconButton>
                  </FilterWrapper>
                ) : null}
              </>
            ),
          }}
          inputRef={inputRef}
          name={name}
          onBlur={compatibilityOnBlur}
          onKeyDown={onKeyDown}
        />
      </FormInputWrapper>
      {anchorEl && showPopup ? (
        <Popper
          anchorEl={anchorEl}
          data-testid={`EAutocomplete-Popper-${name}`}
          open={popupOpen}
          placement='bottom-start'
          role='presentation'
          style={{
            width: dynamicDropdownWidth ? 'auto' : anchorEl.clientWidth,
            zIndex: 'var(--zindex-dropdown)',
          }}
        >
          <StyledListbox
            {...getListboxProps()}
            className={classy({
              alignRight: align === 'right',
              moreOptionPadding,
            })}
            hasMoreAfter={hasMoreAfter}
            hasMoreBefore={hasMoreBefore}
            onLoadMoreAfter={onLoadMoreAfter}
            onLoadMoreBefore={onLoadMoreBefore}
          >
            {optionFilterInfo ? (
              <li className='filter-info'>{optionFilterInfo}</li>
            ) : null}
            <EAutocompleteCategories
              categories={categoriesWithOptions}
              getOptionProps={getOptionProps}
              multiple={multiple}
              onCheckboxClick={(option) => compatibilityOnChange(option)}
            />
            {isLoading ? (
              <CenteredLi>
                <AnimationWrapper>
                  <LoadingIcon />
                </AnimationWrapper>
              </CenteredLi>
            ) : (
              <>
                {optionsToDisplay.length === 0 ? (
                  <li className='disabled'>
                    {t('components.EAutocomplete.noOptions')}
                  </li>
                ) : null}
              </>
            )}
          </StyledListbox>
        </Popper>
      ) : null}
    </>
  );
}

// MUI v7 fires `onHighlightChange` only for user input, so `highlightedOption` is null for
// auto-highlighted options. This hook mirrors MUI's real highlight (kept across popup close)
// so blur/Tab commit the correct option.
function useEffectiveHighlight(
  highlightedOption: EAutocompleteOption | null,
  optionsToDisplay: EAutocompleteOption[]
): React.RefObject<EAutocompleteOption | null> {
  const effectiveHighlightRef = useRef<EAutocompleteOption | null>(null);
  useEffect(() => {
    const current =
      highlightedOption ?? firstSelectableOption(optionsToDisplay);
    if (current) {
      effectiveHighlightRef.current = current;
    }
  }, [highlightedOption, optionsToDisplay]);
  return effectiveHighlightRef;
}

function firstSelectableOption(
  options: EAutocompleteOption[]
): EAutocompleteOption | null {
  return options.find((option) => !option.disabled) ?? null;
}
